import { inject, injectable } from 'inversify'
import { action, computed, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import { HttpGateway } from '@core/services/http.gateway'
import { APP_VERSION } from '@core/version'
import type { ClockGateway } from '@core/services/clock.gateway'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationOwner } from '@core/operations/operation.types'
import {
  defineOperation,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import type { ActionFailurePm } from '@core/feedback/feedback.packing'

// Long enough to be invisible in normal use, short enough that a tab left open
// notices a deploy within a coffee break.
const VERSION_POLL_INTERVAL_MS = 15 * 60 * 1000
const VersionDtoSchema = z.object({ version: z.string() })

/**
 * `ActionFailurePm`, same as every other operation's Failure type — not a
 * bespoke shape. The only branch this operation can ever actually produce is
 * `temporarily-unavailable`, from a hung check that outlives its own
 * timeout: a rejected/unparseable response is still swallowed to a vacuous
 * success below, since there is no user-facing failure state for those.
 */
type VersionCheckFailure = ActionFailurePm

// 10s total timeout — spec §8.1. Retry stays `none` permanently: the next
// scheduled poll tick is the next attempt. `ignore`, not `join`: a tick that
// finds one already in flight has nothing useful to wait for — the
// in-flight check already covers it.
export const VERSION_CHECK_OPERATION = defineOperation<null, VersionCheckFailure>({
  id: operationId('version.check'),
  key: () => operationKey('latest'),
  groups: () => [operationGroup('connectivity')],
  pending: { kind: 'ignore' },
  safety: { kind: 'read' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * Source of truth for the app's own build version against the latest one the
 * backend knows about, so a stale tab can be told to reload.
 */
@injectable()
export class VersionRepository implements AppLifecycle {
  readonly currentVersion = APP_VERSION
  latestVersionPm: string | null = null

  @inject(Types.IClockGateway)
  private clockGateway!: ClockGateway

  private readonly dataGateway: HttpGateway
  private readonly operationOwner: OperationOwner
  private readonly checkOperation: OperationHandle<null, VersionCheckFailure>

  private pollingStarted = false

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.operationOwner = operationRepository.createOwner({ id: 'version.repository', lifetime: 'application' })
    this.checkOperation = this.operationOwner.register(VERSION_CHECK_OPERATION)

    makeObservable(this, {
      latestVersionPm: observable,
      isUpdateAvailable: computed,
      simulateNewVersionAvailable: action,
    })
  }

  // Single source of truth for "the running build is stale" — VersionPresenter
  // and ConnectivityDemoPresenter both read this rather than each re-deriving
  // the same comparison.
  get isUpdateAvailable(): boolean {
    return this.latestVersionPm !== null && this.latestVersionPm !== this.currentVersion
  }

  /**
   * Called once from AppPresenter.load(). Compares the running frontend's
   * APP_VERSION against the version the server reports — both are bumped by
   * hand together, so a mismatch means this tab is running a stale build.
   *
   * Checks immediately and then on an interval, because the case that matters
   * is precisely the tab left open across a deploy: a startup-only check can
   * never notice one. The timer goes through ClockGateway rather than
   * `setInterval` so tests drive it instead of waiting on it.
   *
   * `pollingStarted` guards *interval registration* — a different concern
   * from the check Operation's own `ignore` policy above, which guards
   * *concurrent checks*. Calling initialize() twice must not register a
   * second `everyInterval` timer, independent of whether two checks happen
   * to race.
   */
  initialize(): void {
    if (this.pollingStarted) return
    this.pollingStarted = true

    void this.checkForUpdate()
    this.clockGateway.everyInterval(VERSION_POLL_INTERVAL_MS, () => {
      void this.checkForUpdate()
    })
  }

  // A failed/404 check just means "couldn't tell, stay quiet" rather than
  // surfacing an error — a gateway/validation failure maps to a vacuous
  // success (no commit) rather than `operationFailed`. Only the Coordinator's
  // own timeout (VERSION_CHECK_OPERATION above) can produce a real Failure.
  checkForUpdate = async (): Promise<void> => {
    await this.checkOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/version', { signal, idempotencyKey: null })
        if (!response.success) return operationSucceeded(null)

        const parsed = VersionDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationSucceeded(null)

        return operationSucceeded(parsed.data.version)
      },
      {
        succeeded: (version) => {
          if (version !== null) this.latestVersionPm = version
        },
        failed: null,
      },
    )
  }

  // Demo-only bypass: exercises the banner immediately without waiting for a
  // deploy or for the next polling interval.
  simulateNewVersionAvailable(): void {
    this.latestVersionPm = `${this.currentVersion}-simulated`
  }
}
